// Survey statistics for country-level estimates.
//
// Scores are weighted means over respondents. Reporting them without a margin of
// error invites reading rank differences that the sample cannot support: unfiltered
// a country is estimated to about +/-1.1 points, but a four-way demographic filter
// can leave a handful of respondents, at which point the error exceeds the entire
// spread between countries.

const Z_95 = 1.959964;

/** Mean of `value` over `rows`, weighted by the survey weight `W`. */
export const weightedMean = (rows, value) => {
	let sumW = 0;
	let sumWX = 0;

	for (const row of rows) {
		sumW += row.W;
		sumWX += row.W * value(row);
	}

	return sumW === 0 ? NaN : sumWX / sumW;
};

/**
 * Margin of error of a weighted mean, at 95%.
 *
 * Unequal weights cost precision, so the variance carries Kish's design effect,
 * deff = n * sum(w^2) / sum(w)^2. Dividing n by it gives the effective sample size:
 * the number of equally weighted respondents that would be this informative.
 */
export const weightedUncertainty = (rows, value) => {
	const n = rows.length;
	if (n < 2) return { mean: n === 1 ? value(rows[0]) : NaN, moe: NaN, effectiveN: n };

	const mean = weightedMean(rows, value);

	let sumW = 0;
	let sumW2 = 0;
	let sumWDev2 = 0;

	for (const row of rows) {
		const deviation = value(row) - mean;
		sumW += row.W;
		sumW2 += row.W * row.W;
		sumWDev2 += row.W * deviation * deviation;
	}

	const variance = (sumWDev2 / sumW) * (n / (n - 1));
	const designEffect = (n * sumW2) / (sumW * sumW);
	const effectiveN = n / designEffect;

	return {
		mean,
		moe: Z_95 * Math.sqrt(variance / effectiveN),
		effectiveN,
	};
};

/**
 * How many of `others` this estimate cannot be told apart from, by overlapping
 * confidence intervals. A rank means little when fifty countries share it.
 */
export const indistinguishableCount = (item, others) =>
	others.filter(
		(other) =>
			other.country !== item.country &&
			other.ciLow <= item.ciHigh &&
			other.ciHigh >= item.ciLow
	).length;
